import CruiseFilterFooter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterFooter'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type { DeparturePortMapped } from './CruiseDepartureSelect.types'
import CruiseDepartureSelectList from './CruiseDepartureSelectList'
import { useCruiseSearchInput } from '../CruiseSearchInputContext'
import CruiseDepartureSelectListAll from './CruiseDepartureSelectListAll'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import ChipMultiSelectInput from 'components/Common/Form/Input/ChipMultiSelectInput'

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  drawerMode?: boolean
  regions: Array<App.CruiseDepartureRegion>
  setIsAllSelected: (value: boolean) => void
  isAllSelected: boolean
  setSearchTerm: (value: string) => void
  searchTerm: string
  handleSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnChange: (e: Array<App.SearchItem>) => void
  fetching: boolean
  searchDeparturePorts: Array<DeparturePortMapped>
  selectedRegions: Array<string>
  setSelectedRegions: (value: Array<string>) => void
  onRemoveSelectedOption: (value: string) => void
  setSelectedRegion: (value: App.CruiseDepartureRegion) => void
  filterFacetsByRegion: (region: App.CruiseDepartureRegion) => Array<App.PortFacetV2Item>
  mapDeparturePort: (departurePort: App.PortFacetV2Item) => DeparturePortMapped
  isChipMode?: boolean
  initialFilters?: App.CruiseInitialFilters
  disableAutoFocus?: boolean
}

const SET_SECONDARY_SEARCH_ITEMS = GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEMS

function CruiseDepartureSelectMobile({
  regions,
  onChange,
  setIsAllSelected,
  isAllSelected,
  setSearchTerm,
  searchTerm,
  handleSearchTerm,
  handleOnChange,
  fetching,
  searchDeparturePorts,
  selectedRegions,
  setSelectedRegions,
  onRemoveSelectedOption,
  setSelectedRegion,
  filterFacetsByRegion,
  mapDeparturePort,
  isChipMode,
  initialFilters,
  disableAutoFocus,
}: Props) {
  const intl = useIntl()
  const inputSearchRef = useRef<HTMLInputElement>(null)
  const globalSearchState = useContext(GlobalSearchStateContext)
  const { secondarySearchItems } = globalSearchState
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const { isMobileOpen, closeMobile, toggleMobile } = useCruiseSearchInput()

  useEffect(() => {
    if (regions[0]) {
      setSelectedRegion(regions[0])
    }
    if (isMobileOpen || !disableAutoFocus) {
      inputSearchRef.current?.focus({ preventScroll: true })
    }
  }, [regions, setSelectedRegion, isMobileOpen, disableAutoFocus])

  const onClear = useCallback(() => {
    searchDispatch({
      type: SET_SECONDARY_SEARCH_ITEMS,
      secondarySearchItems: [],
    })
    setSearchTerm('')
    setIsAllSelected(false)
    setSelectedRegions([])
  }, [searchDispatch, setIsAllSelected, setSearchTerm, setSelectedRegions])

  const handleOnApply = useCallback(() => {
    onChange({ secondarySearchItems })
    setSearchTerm('')
    closeMobile()
  }, [onChange, secondarySearchItems, closeMobile, setSearchTerm])

  const handleSelectAll = useCallback(() => {
    searchDispatch({
      type: SET_SECONDARY_SEARCH_ITEMS,
      secondarySearchItems: [],
    })
    setIsAllSelected(!isAllSelected)
  }, [searchDispatch, setIsAllSelected, isAllSelected])

  const inputValue = useMemo(() => {
    if (isMobileOpen) return searchTerm
    if (secondarySearchItems.length > 1) {
      const names = secondarySearchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${secondarySearchItems.length - 1}`
    }
    return secondarySearchItems.map((item) => item.format.mainText).join(', ')
  }, [secondarySearchItems, isMobileOpen, searchTerm])

  const selectedOptions = useMemo(() => secondarySearchItems.map((item) => ({
    value: item.value,
    label: item.format.mainText,
  })), [secondarySearchItems])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [setSearchTerm])

  const searchInputEndIcon = useMemo(() => {
    if (!searchTerm.length) {
      return <LineSearchIcon />
    }

    return <IconButton kind="tertiary" onClick={handlerClearSearchTerm}><LineTimesIcon /></IconButton>
  }, [handlerClearSearchTerm, searchTerm.length])

  const callback = useCallback((region: App.CruiseDepartureRegion) =>
    filterFacetsByRegion(region).map(mapDeparturePort),
  [filterFacetsByRegion, mapDeparturePort])

  const departingFromLabel = intl.formatMessage({
    id: 'DEnpcloyPc_dk_gOiBh4',
    defaultMessage: 'Departing from',
  })

  const searchDepartingFromPlaceholder = intl.formatMessage({
    id: 'N5nPB-J4sJV1-Mdfb8gk',
    defaultMessage: 'Search departing from',
  })

  const allDeparturePortsPlaceholder = intl.formatMessage({
    id: 'w4vyvXfeeK4IN36vN05F',
    defaultMessage: 'All departure ports',
  })

  const departurePortsTitle = intl.formatMessage({
    id: '0ZmGOGirD_xAWVQqNxAE',
    defaultMessage: 'Departure ports',
  })

  const searchDeparturePortsPlaceholder = intl.formatMessage({
    id: 'b8V4xXwgIiZfq8AkT3DR',
    defaultMessage: 'Search departure ports',
  })

  return <div data-testid="cruise-departure-select-mobile">
    <CruiseFilterInput
      isOpen={isMobileOpen}
      data-testid="cruise-departure-select-mobile-input"
      label={departingFromLabel}
      placeholder={isMobileOpen ? searchDepartingFromPlaceholder : allDeparturePortsPlaceholder}
      inputValue={inputValue}
      formFieldRef={inputSearchRef}
      selectedItems={secondarySearchItems.length}
      onClick={() => {
        trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_departure_filter',
          type: 'interaction',
        })
        toggleMobile()
      }}
      isChipMode={isChipMode}
    />

    <ModalBase
      mode="drawer"
      isOpen={isMobileOpen}
      onClose={closeMobile}
    >
      <ModalHeader
        title={departurePortsTitle}
        onCloseButtonClick={closeMobile}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <Group direction="vertical" gap={16}>
            <ChipMultiSelectInput
              ref={inputSearchRef}
              selectedOptions={selectedOptions}
              placeholder={searchDeparturePortsPlaceholder}
              value={searchTerm}
              onChange={handleSearchTerm}
              onRemoveOption={onRemoveSelectedOption}
              endIcon={searchInputEndIcon}
              noValidationSpacing
            />

            <CSSBreakpoint max="mobile">
              {(!searchTerm) && <CruiseDepartureSelectListAll
                isSelected={isAllSelected}
                onClick={handleSelectAll}
              />}
            </CSSBreakpoint>

            {searchTerm && <CruiseDepartureSelectList
              inputSearchRef={inputSearchRef}
              searchTerm={searchTerm}
              secondarySearchItems={secondarySearchItems}
              isAllSelected={isAllSelected}
              selectedRegions={selectedRegions}
              onChange={handleOnChange}
              selectedRegion={regions[0]}
              setIsAllSelected={setIsAllSelected}
              setSelectedRegions={setSelectedRegions}
              isMobileMode
              fetching={fetching}
              searchDeparturePorts={searchDeparturePorts}
              setSelectedRegion={setSelectedRegion}
              regions={regions}
            />}

            {!searchTerm && regions.map((region) => <div key={region.identifier}>
              <CruiseDepartureSelectList
                topLevelRegionOnly
                inputSearchRef={inputSearchRef}
                searchTerm={searchTerm}
                secondarySearchItems={secondarySearchItems}
                isAllSelected={isAllSelected}
                selectedRegions={selectedRegions}
                onChange={handleOnChange}
                selectedRegion={region}
                setIsAllSelected={setIsAllSelected}
                setSelectedRegions={setSelectedRegions}
                isMobileMode
                fetching={fetching}
                searchDeparturePorts={callback(region)}
                setSelectedRegion={setSelectedRegion}
                regions={regions}
              />
            </div>)}
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <CruiseFilterFooter
          onApply={() => {
            trackClientEvent({
              subject: 'filter_applied',
              action: 'applied',
              category: 'cruise_departure_filter',
              type: 'interaction',
            })

            trackEvent(searchEventWithContext({
              searchEvent: { type: 'filters' },
              sortContext: { value: 'recommended' },
              searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
              filterContexts: secondarySearchItems.map(item => ({
                label: 'Departure Port',
                type: 'departure_port',
                value: item.value,
              })),
            }))
            handleOnApply()
          }}
          onClear={() => {
            trackClientEvent({
              subject: 'filter_cleared',
              action: 'cleared',
              category: 'cruise_departure_filter',
              type: 'interaction',
            })
            onClear()
          }}
          initialFilters={initialFilters}
        />
      </ModalFooter>
    </ModalBase>
  </div>
}

export default CruiseDepartureSelectMobile
